import { useEffect } from "react";
import { Button, CloseButton, Group, Paper, Stack, Text } from "@mantine/core";
import { observer } from "mobx-react";
import { signInCopy } from "@/core/signInCopy";
import { signInFunnel } from "@/core/signInFunnel";
import { useStores } from "@/hooks/useStores";

interface SignInPromptProps {
    /** One short line, for the player */
    compact?: boolean;
}

/**
 * Tells a signed-out listener that where they are in episodes is only remembered when signed in.
 * Shown only where sign-in is switched on, not for a while after any prompt was closed (`promptQuiet`),
 * and not after the listener signed out on purpose.
 */
export const SignInPrompt = observer(({ compact = false }: SignInPromptProps) => {
    const { appStore, authStore } = useStores();

    // One prompt per page: the banner on the home page takes the place of the line in the player
    const visible =
        appStore.signInEnabled &&
        authStore.status === "anonymous" &&
        !authStore.signedOutThisVisit &&
        !appStore.signInPromptDismissed &&
        !(compact && appStore.homeBannerVisible);
    const source = compact ? "player" : "home-banner";

    useEffect(() => {
        if (compact || !visible) return;
        appStore.setHomeBannerVisible(true);
        return () => appStore.setHomeBannerVisible(false);
    }, [compact, visible, appStore]);

    useEffect(() => {
        if (visible) signInFunnel.promptShown(source);
    }, [visible, source]);

    if (!visible) {
        return null;
    }

    const open = () => appStore.openSignIn(source);

    if (compact) {
        return (
            <Group gap="xs" wrap="nowrap">
                <Text c="dimmed" size="xs">
                    {signInCopy.player}
                </Text>
                <Button onClick={open} size="compact-xs" variant="subtle">
                    Logga in
                </Button>
            </Group>
        );
    }

    return (
        <Paper
            aria-label="Logga in för att synka mellan enheter"
            component="section"
            mb="lg"
            p="md"
            radius="md"
            withBorder>
            <Group justify="space-between" wrap="nowrap">
                <Stack gap={4}>
                    <Text fw={600}>{signInCopy.banner.title}</Text>
                    <Text>{signInCopy.banner.text}</Text>
                </Stack>
                <Group gap="xs" wrap="nowrap">
                    <Button color="teal" onClick={open} variant="light">
                        Logga in
                    </Button>
                    <CloseButton aria-label="Stäng" onClick={() => appStore.dismissSignInPrompt()} />
                </Group>
            </Group>
        </Paper>
    );
});
